/*
 * Shared focus-trap helpers that survive a missing global.js (2026-10-08).
 *
 * trapFocus / removeTrapFocus live in global.js. When global.js never runs
 * (bots, in-app WebViews, blocked or failed loads) or crashes partway, every
 * dialog that called them bare threw: the dialog opened without a focus trap
 * (screen-reader contract C1) and close() threw before clearing
 * body.overflow-hidden and returning focus.
 *
 * mdTrapFocus / mdRemoveTrapFocus delegate to global.js when it is present
 * (identical behaviour) and otherwise run a verbatim copy of its logic. The
 * fallback shares window.trapFocusHandlers, so a trap set by either copy is
 * released by both.
 *
 * CONSUMERS: cart-drawer.js, details-modal.js, pickup-availability.js,
 * cart-notification.js. Each still guards its calls with
 * `typeof mdTrapFocus === 'function'` and falls back to a plain .focus(), so a
 * missing copy of THIS file cannot throw either.
 *
 * LOAD ORDER: loaded with defer directly after global.js in the <head> of
 * layout/theme.liquid and layout/password.liquid. Every consumer's tag sits
 * later in the document (body sections/snippets, or after this tag in
 * password.liquid's head), and defer scripts run in document order.
 *
 * Kept in step with global.js by tests/assets/focus-trap-fallback.test.js.
 */
function mdGetFocusableElements(container) {
  const elements = Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
  // Drop what cannot take focus because it is not rendered: display:none on
  // it or an ancestor, closed <details> content, visibility:hidden. Counting
  // those let a trap wrap onto an unfocusable element so focus stayed put
  // (the product media modal's hidden deferred poster, 2026-10-08).
  // checkVisibility() is needed: Chrome reports client rects for closed
  // <details> content, so getClientRects() is only the fallback. A container
  // that is itself not rendered (not laid out yet, or no layout engine) gives
  // nothing to judge by, so the full list is kept.
  const rendered = (el) =>
    typeof el.checkVisibility === 'function'
      ? el.checkVisibility({
          visibilityProperty: true,
          checkVisibilityCSS: true,
        })
      : el.getClientRects().length > 0;
  if (!rendered(container)) return elements;
  return elements.filter(rendered);
}

function mdRemoveTrapFocus(elementToFocus = null) {
  if (typeof removeTrapFocus === 'function') {
    // global.js can crash partway, AFTER hoisting removeTrapFocus but BEFORE
    // it creates window.trapFocusHandlers; its removeTrapFocus would then
    // throw on the undefined object. Ensure it first (#2088 review).
    window.trapFocusHandlers = window.trapFocusHandlers || {};
    removeTrapFocus(elementToFocus);
    return;
  }
  const handlers = (window.trapFocusHandlers = window.trapFocusHandlers || {});
  document.removeEventListener('focusin', handlers.focusin);
  document.removeEventListener('focusout', handlers.focusout);
  document.removeEventListener('keydown', handlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function mdTrapFocus(container, elementToFocus = container) {
  if (typeof trapFocus === 'function') {
    window.trapFocusHandlers = window.trapFocusHandlers || {};
    trapFocus(container, elementToFocus);
    return;
  }
  const handlers = (window.trapFocusHandlers = window.trapFocusHandlers || {});
  // The bounds are read on every focusin / keydown, not once here: callers
  // trap before their content is shown (the menu drawer traps before its
  // <details> opens, so its links are not rendered yet) and some re-render
  // while open (cart drawer), so a list taken now can be stale or empty.
  const bounds = () => {
    const elements = mdGetFocusableElements(container);
    return {
      first: elements[0],
      last: elements[elements.length - 1],
      // A start target outside the tab order (e.g. a tabindex=-1 heading)
      // must arm the trap too, or Shift+Tab from it escapes the dialog.
      start: elements.includes(elementToFocus) ? null : elementToFocus,
    };
  };
  const armsTrap = (target) => {
    const { first, last, start } = bounds();
    return (
      target === container ||
      target === start ||
      target === last ||
      target === first
    );
  };

  mdRemoveTrapFocus();

  handlers.focusin = (event) => {
    if (!armsTrap(event.target)) return;

    document.addEventListener('keydown', handlers.keydown);
  };

  handlers.focusout = function () {
    document.removeEventListener('keydown', handlers.keydown);
  };

  handlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    const { first, last, start } = bounds();
    // Nothing focusable inside (e.g. an empty cart): leave Tab alone.
    if (!first || !last) return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if (
      (event.target === container ||
        event.target === start ||
        event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', handlers.focusout);
  document.addEventListener('focusin', handlers.focusin);

  elementToFocus.focus();

  // Already focused before the trap was set (the menu and facets drawer
  // summary the customer just activated): focus() fired no focusin, so arm
  // the keydown here or Shift+Tab right after open escapes. Adding the same
  // listener twice is a no-op, and focusout still removes it.
  if (armsTrap(document.activeElement))
    document.addEventListener('keydown', handlers.keydown);

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}
